import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api'; 
import { Building2, Mail, Phone, ArrowLeft, MapPin, Briefcase, Users, Calendar, User } from 'lucide-react';
import AdminLayout from '../layouts/AdminLayout'; 
import PageTitle from '../components/ui/PageTitle'; 

export default function EmployerView() {
    const { id } = useParams(); 
    const navigate = useNavigate();
    
    const [employer, setEmployer] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [candidates, setCandidates] = useState([]); // 🌟 Naya state candidates ke liye
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchEmployerDetails = async () => {
            const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
            try {
                const response = await api.get(`/admin/employers/${id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (response.data.status === 'success') {
                    setEmployer(response.data.employer);
                    
                    const fetchedJobs = response.data.jobs || [];
                    setJobs(fetchedJobs);

                    // 🌟 Saare jobs se candidates ko nikal kar ek list bana rahe hain
                    let allCandidates = [];
                    fetchedJobs.forEach(job => {
                        if (job.candidates && job.candidates.length > 0) {
                            job.candidates.forEach(cand => {
                                allCandidates.push({
                                    ...cand,
                                    applied_for: job.title // Candidate ne kis job ke liye apply kiya
                                });
                            });
                        }
                    });
                    setCandidates(allCandidates);
                }
            } catch (error) {
                console.error("Error fetching employer details:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchEmployerDetails();
    }, [id]);

    return (
        <AdminLayout>
            <div className="max-w-7xl mx-auto space-y-6 pb-10">
                
                {/* Back Button */}
                <button 
                    onClick={() => navigate(-1)} 
                    className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-black transition-colors mb-2"
                >
                    <ArrowLeft size={16} /> Back to Manage Employers
                </button>

                <PageTitle 
                    title="Employer Overview" 
                    subtitle="Complete details of employer, their posted jobs, and candidates." 
                />

                {loading ? (
                    <div className="p-12 text-center text-gray-500 font-medium bg-white rounded-2xl border border-gray-100 shadow-sm">
                        Loading employer profile...
                    </div>
                ) : !employer ? (
                    <div className="p-12 text-center text-red-500 font-medium bg-white rounded-2xl border border-red-100 shadow-sm">
                        Employer not found!
                    </div>
                ) : (
                    <div className="space-y-6">
                        
                        {/* 🌟 STATS CARDS */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                                    <Building2 size={28} />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 font-bold uppercase">Account Status</p>
                                    <h4 className={`text-lg font-bold uppercase mt-1 ${employer.status === 'active' ? 'text-green-600' : 'text-red-500'}`}>
                                        {employer.status}
                                    </h4>
                                </div>
                            </div>
                            
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-yellow-50 flex items-center justify-center text-yellow-600">
                                    <Briefcase size={28} />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 font-bold uppercase">Total Jobs Posted</p>
                                    <h4 className="text-2xl font-bold text-gray-900 mt-1">{jobs.length}</h4>
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-purple-50 flex items-center justify-center text-purple-600">
                                    <Users size={28} />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 font-bold uppercase">Total Candidates</p>
                                    <h4 className="text-2xl font-bold text-gray-900 mt-1">{candidates.length}</h4>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            
                            {/* 🌟 LEFT COLUMN: EMPLOYER BASIC INFO */}
                            <div className="lg:col-span-1 space-y-6">
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                    <div className="bg-[#FED547] h-20"></div>
                                    <div className="px-6 pb-6 relative">
                                    
                                        <div className="mt-12">
                                            <h2 className="text-xl font-bold text-gray-900">{employer.company_name}</h2>
                                            <p className="text-sm text-gray-500 font-medium flex items-center gap-1 mt-1">
                                                <User size={14} /> Contact Person: {employer.name}
                                            </p>
                                        </div>

                                        <div className="mt-6 space-y-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500">
                                                    <Mail size={16} />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-400 font-bold uppercase">Email</p>
                                                    <p className="text-sm font-semibold text-gray-800">{employer.email}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500">
                                                    <Phone size={16} />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-400 font-bold uppercase">Phone</p>
                                                    <p className="text-sm font-semibold text-gray-800">{employer.phone}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500">
                                                    <MapPin size={16} />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-400 font-bold uppercase">City</p>
                                                    <p className="text-sm font-semibold text-gray-800">{employer.city || 'Not Provided'}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500">
                                                    <Calendar size={16} />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-400 font-bold uppercase">Joined</p>
                                                    <p className="text-sm font-semibold text-gray-800">
                                                        {new Date(employer.created_at).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 🌟 RIGHT COLUMN: JOBS & CANDIDATES LIST */}
                            <div className="lg:col-span-2 space-y-6">
                                
                                {/* --- JOBS POSTED SECTION --- */}
                                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                        <Briefcase size={20} className="text-[#FED547]"/> Jobs Posted by {employer.company_name}
                                    </h3>
                                    
                                    {jobs.length === 0 ? (
                                        <p className="text-sm text-gray-500 py-4 bg-gray-50 rounded-xl text-center border border-dashed border-gray-200">
                                            This employer hasn't posted any jobs yet.
                                        </p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse">
                                                <thead>
                                                    <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                                                        <th className="p-3 font-bold rounded-tl-lg">Job Title</th>
                                                        <th className="p-3 font-bold">Location</th>
                                                        <th className="p-3 font-bold">Status</th>
                                                        <th className="p-3 font-bold text-center rounded-tr-lg">Applications</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {jobs.map((job, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50/50">
                                                            <td className="p-3">
                                                                <p className="font-bold text-sm text-gray-800">{job.title}</p>
                                                                <p className="text-xs text-gray-500">{job.type || 'Full Time'}</p>
                                                            </td>
                                                            <td className="p-3 text-sm text-gray-600">{job.location || 'Remote'}</td>
                                                            <td className="p-3">
                                                                <span className="px-2 py-1 bg-green-50 text-green-700 text-[10px] font-bold rounded-full uppercase">
                                                                    Active
                                                                </span>
                                                            </td>
                                                            <td className="p-3 text-center">
                                                                <span className="font-bold text-gray-800">{job.candidates?.length || 0}</span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                                {/* --- CANDIDATES LIST SECTION --- */}
                                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                        <Users size={20} className="text-blue-500"/> Candidates Applied
                                    </h3>
                                    
                                    {candidates.length === 0 ? (
                                        <p className="text-sm text-gray-500 py-4 bg-gray-50 rounded-xl text-center border border-dashed border-gray-200">
                                            No candidates have applied to these jobs yet.
                                        </p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse">
                                                <thead>
                                                    <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                                                        <th className="p-3 font-bold rounded-tl-lg">Candidate Name</th>
                                                        <th className="p-3 font-bold">Contact Info</th>
                                                        <th className="p-3 font-bold rounded-tr-lg">Applied For (Job)</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {candidates.map((cand, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50/50">
                                                            <td className="p-3">
                                                                <p className="font-bold text-sm text-gray-800">{cand.name}</p>
                                                            </td>
                                                            <td className="p-3">
                                                                <p className="text-xs text-gray-600 flex items-center gap-1"><Mail size={12}/> {cand.email}</p>
                                                                <p className="text-xs text-gray-600 flex items-center gap-1 mt-1"><Phone size={12}/> {cand.phone}</p>
                                                            </td>
                                                            <td className="p-3">
                                                                <span className="text-xs font-semibold bg-yellow-50 text-yellow-700 px-2.5 py-1 rounded-md">
                                                                    {cand.applied_for}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}